import React, { useState } from 'react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { TargetedImagePicker } from '@/components/media/TargetedImagePicker';
import { CroppedThumbnail } from '@/components/media/CroppedThumbnail';
import { pendingKey, type PendingTargeted } from '@/types/media';
import { ASSET_GROUP_LIMITS } from '@/types/assetGroup';
import { ImageThumbnailWithActions } from '@/components/common/ImageThumbnailWithActions';

interface AssetGroupImagesStepProps {
  landscapePending: PendingTargeted[];
  squarePending: PendingTargeted[];
  logoPending: PendingTargeted[];
  onChangeLandscapePending: (pending: PendingTargeted[]) => void;
  onChangeSquarePending: (pending: PendingTargeted[]) => void;
  onChangeLogoPending: (pending: PendingTargeted[]) => void;
  errors?: {
    landscapeImages?: string;
    squareImages?: string;
    logoImages?: string;
  };
}

// Pending-crop preview — CSS-cropped (no GCS write yet). Materialization
// happens when the wizard submits.
interface PendingPreviewProps {
  pending: PendingTargeted;
  onRemove: () => void;
  aspectRatio?: '1.91:1' | '1:1';
}

function PendingPreview({ pending, onRemove, aspectRatio = '1:1' }: PendingPreviewProps) {
  const size = {
    width: aspectRatio === '1.91:1' ? 76 : 64,
    height: aspectRatio === '1.91:1' ? 40 : 64,
  };
  return (
    <ImageThumbnailWithActions
      alt={pending.source.alt_text || pending.source.original_filename}
      size={size}
      onRemove={onRemove}
      removeLabel="Remove image"
      confirmTitle="Remove Image"
      confirmMessage="Are you sure you want to remove this image from the asset group?"
    >
      <CroppedThumbnail source={pending.source} cropBox={pending.cropBox} />
    </ImageThumbnailWithActions>
  );
}

export const AssetGroupImagesStep: React.FC<AssetGroupImagesStepProps> = ({
  landscapePending,
  squarePending,
  logoPending,
  onChangeLandscapePending,
  onChangeSquarePending,
  onChangeLogoPending,
  errors,
}) => {
  // Image picker state. Targeted creation is **deferred** until the
  // wizard's submit handler — picking + cropping here just updates the
  // parent form's PendingTargeted arrays, no GCS writes.
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [imagePickerMode, setImagePickerMode] = useState<'landscape' | 'square' | 'logo'>('landscape');

  const openImagePicker = (mode: typeof imagePickerMode) => {
    setImagePickerMode(mode);
    setShowImagePicker(true);
  };

  const handleImageConfirm = (pending: PendingTargeted[]) => {
    switch (imagePickerMode) {
      case 'landscape':
        onChangeLandscapePending(pending);
        break;
      case 'square':
        onChangeSquarePending(pending);
        break;
      case 'logo':
        onChangeLogoPending(pending);
        break;
    }
    setShowImagePicker(false);
  };

  const placementForMode: Record<typeof imagePickerMode, string> = {
    landscape: 'marketing_image',
    square: 'square_marketing_image',
    logo: 'logo',
  };
  const MAX_SELECTION_FOR_MODE: Record<typeof imagePickerMode, number> = {
    landscape: ASSET_GROUP_LIMITS.landscapeImage.max,
    square: ASSET_GROUP_LIMITS.squareImage.max,
    logo: ASSET_GROUP_LIMITS.logoImage.max,
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Image Assets</h3>
        <p className="text-sm text-gray-600">
          Select images from your Media Library. Google will use these images across different ad formats and placements.
        </p>
      </div>

      {/* Info Banner */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start">
          <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
            <PhotoIcon className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <span className="block text-sm font-medium text-blue-900">Performance Max Image Requirements</span>
            <ul className="text-xs text-blue-700 mt-1 list-disc list-inside space-y-1">
              <li>At least 1 landscape image (1.91:1 ratio, min 600x314px)</li>
              <li>At least 1 square image (1:1 ratio, min 300x300px)</li>
              <li>At least 1 logo image (1:1 ratio, min 128x128px)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Landscape Images (Required) */}
      <div>
        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Landscape Marketing Images (1.91:1) <span className="text-red-500">*</span>
          </label>
          <span className="text-xs text-gray-500">
            {landscapePending.length}/{ASSET_GROUP_LIMITS.landscapeImage.max} (min {ASSET_GROUP_LIMITS.landscapeImage.min} required)
          </span>
        </div>
        <div className="flex flex-wrap gap-2 mb-2">
          {landscapePending.map((p) => (
            <PendingPreview
              key={pendingKey(p)}
              pending={p}
              aspectRatio="1.91:1"
              onRemove={() => onChangeLandscapePending(landscapePending.filter((q) => pendingKey(q) !== pendingKey(p)))}
            />
          ))}
          <button
            type="button"
            onClick={() => openImagePicker('landscape')}
            className="w-20 h-10 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
            aria-label="Add landscape image"
          >
            <PhotoIcon className="w-5 h-5" />
          </button>
        </div>
        {errors?.landscapeImages && (
          <p className="text-sm text-red-600">{errors.landscapeImages}</p>
        )}
        <p className="text-xs text-gray-500">Recommended: 1200x628px, max 5MB</p>
      </div>

      {/* Square Images (Required) */}
      <div>
        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Square Marketing Images (1:1) <span className="text-red-500">*</span>
          </label>
          <span className="text-xs text-gray-500">
            {squarePending.length}/{ASSET_GROUP_LIMITS.squareImage.max} (min {ASSET_GROUP_LIMITS.squareImage.min} required)
          </span>
        </div>
        <div className="flex flex-wrap gap-2 mb-2">
          {squarePending.map((p) => (
            <PendingPreview
              key={pendingKey(p)}
              pending={p}
              aspectRatio="1:1"
              onRemove={() => onChangeSquarePending(squarePending.filter((q) => pendingKey(q) !== pendingKey(p)))}
            />
          ))}
          <button
            type="button"
            onClick={() => openImagePicker('square')}
            className="w-16 h-16 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
            aria-label="Add square image"
          >
            <PhotoIcon className="w-5 h-5" />
          </button>
        </div>
        {errors?.squareImages && (
          <p className="text-sm text-red-600">{errors.squareImages}</p>
        )}
        <p className="text-xs text-gray-500">Recommended: 1200x1200px, max 5MB</p>
      </div>

      {/* Logo Images (Required) */}
      <div>
        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Logo Images (1:1) <span className="text-red-500">*</span>
          </label>
          <span className="text-xs text-gray-500">
            {logoPending.length}/{ASSET_GROUP_LIMITS.logoImage.max} (min {ASSET_GROUP_LIMITS.logoImage.min} required)
          </span>
        </div>
        <div className="flex flex-wrap gap-2 mb-2">
          {logoPending.map((p) => (
            <PendingPreview
              key={pendingKey(p)}
              pending={p}
              aspectRatio="1:1"
              onRemove={() => onChangeLogoPending(logoPending.filter((q) => pendingKey(q) !== pendingKey(p)))}
            />
          ))}
          <button
            type="button"
            onClick={() => openImagePicker('logo')}
            className="w-12 h-12 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
            aria-label="Add logo image"
          >
            <PhotoIcon className="w-4 h-4" />
          </button>
        </div>
        {errors?.logoImages && (
          <p className="text-sm text-red-600">{errors.logoImages}</p>
        )}
        <p className="text-xs text-gray-500">Recommended: 1200x1200px, min 128x128px</p>
      </div>

      {/* Image Picker Modal — returns PendingTargeted[]; no GCS write here. */}
      <TargetedImagePicker
        isOpen={showImagePicker}
        onClose={() => setShowImagePicker(false)}
        usageContext={{
          platform: 'google_ads',
          placementKey: `google_ads.${placementForMode[imagePickerMode]}`,
        }}
        onConfirm={handleImageConfirm}
        maxSelection={MAX_SELECTION_FOR_MODE[imagePickerMode]}
      />
    </div>
  );
};
